import { createApiClient } from "@app/utils/apiClient";

const peopleGraphApi = createApiClient("https://pgc.adobe.io/api", null, 1, {
  "Content-Type": "application/json",
  "x-api-key": process.env.CLIENT_ID || "bestory-prod",
  "X-Feature-CachingEnabled": true,
});

/**
 * Suggest a person based on the input value.
 *
 * @param {string} value - The input value to suggest a person for.
 * @param {Object} param1 - Additional parameters for the suggestion.
 * @param {"INVITE"|"MENTION"} [param1.relationship="INVITE"] - The relationship type for the suggestion ("INVITE" or "MENTION").
 * @param {string} [param1.resourceUrl] - The resource URL for the suggestion.
 * @param {Object} [axiosConfig={}] - Additional Axios configuration options.
 *
 * @returns {Promise<Object>} - The API response containing the suggested person(s).
 * @throws {Error} - Throws an error if the API request fails.
 */
export const suggest = async (
  value,
  {
    relationship = "INVITE",
    resourceUrl = new URL(window.location.pathname, "https://bestory.adobe.com")
      .href,
  } = {},
  axiosConfig = {},
) => {
  return peopleGraphApi.post(
    "/suggestions",
    {
      relationship,
      resourceType: "application/vnd.adobe.bestory",
      resourceUrl,
      query: {
        value,
        type: ["PERSON"],
      },
    },
    axiosConfig,
  );
};

/**
 * Accept a list of tokens.
 *
 * @param {Array<string>} tokens - The list of tokens to accept.
 * @param {Object} [axiosConfig={}] - Additional Axios configuration options.
 *
 * @returns {Promise<Object>} - The API response containing the accepted tokens.
 * @throws {Error} - Throws an error if the API request fails.
 */
export const accept = async (tokens, axiosConfig = {}) => {
  return peopleGraphApi.post(
    "/accept",
    {
      tokens,
    },
    axiosConfig,
  );
};

/**
 *
 * @param {string} email - The email address of the person to retrieve.
 * @param {Object} [axiosConfig={}] - Additional Axios configuration options.
 *
 * @returns {Promise<Object|null>} - The person data if found, otherwise null.
 * @throws {Error} - Throws an error if the API request fails.
 */
export const getPerson = async (email, axiosConfig = {}) => {
  const response = await suggest(email, {}, axiosConfig);

  return response?.data?._embedded?.suggestions?.[0] || null;
};

/**
 *
 * @param {Array<string>} emails - An array of email addresses to retrieve person data for.
 * @param {Object} [axiosConfig={}] - Additional Axios configuration options.
 *
 * @returns {Promise<Array<Object|null>>} - The person data for each email, or null if not found.
 * @throws {Error} - Throws an error if the API request fails.
 */
export const getPeople = async (emails, axiosConfig = {}) => {
  const suggestions = (
    await Promise.all(emails.map((email) => getPerson(email, axiosConfig)))
  ).filter(Boolean);

  if (!suggestions || suggestions.length === 0) {
    return [];
  }

  const accepted = await accept(
    suggestions.map((s) => s.token),
    axiosConfig,
  );
  const people = suggestions.map((suggestion, idx) => ({
    ...suggestion,
    email:
      accepted?.data?._embedded?.successes?.[idx]?.email || suggestion.caption,
  }));

  return people;
};

export default {
  suggest,
  accept,
  getPerson,
  getPeople,
};
